/* global __DEV__ */

import getConfig from 'next/config';
import Fetch from 'fetch-ponyfill';

import WebSocket from '../network/WebSocket';
import { getTradingViewLocale } from './locales';
import makeApiRequest from './makeApiRequest';
import { wssUri as _getKlinesWsUri } from '../network/handleInitTask';

const { fetch } = Fetch();

const {
  publicRuntimeConfig: {
    site: SITE,
    siteApiHost,
  },
} = getConfig();

const WS_PATH = '/stream?streams=';
const REST_BASE_URI = siteApiHost;
const EXCHANGE_TIMEZONE = 'Asia/Shanghai';
const SERVERTIME_API_PATH = '/api/v1/time';

const TV_RESOLUTIONS_TO_BINANCE = {
  '1': '1m',
  '3': '3m',
  '5': '5m',
  '15': '15m',
  '30': '30m',
  '60': '1h',
  '120': '2h',
  '240': '4h',
  '360': '6h',
  '720': '12h',
  'D': '1d',
  '1W': '1w',
  '1M': '1M',
};

const getKlinesWsUri = async (symbolName, interval) => {
  const streamUrl = _getKlinesWsUri();
  return (
    `${streamUrl.replace(/\/ws$/, WS_PATH)}` +
    `${symbolName.toLowerCase()}@kline_${interval}`
  );
};

const convertKlineArrToBar = (arr) => {
  arr = arr.map(num => +num);
  const [time, open, high, low, close, volume] = arr;
  return {
    time,
    close,
    open,
    high,
    low,
    volume,
  };
};

const convertKlineObjToBar = ({ k: kline }) => {
  const {
    t, l, c, h, o, v,
  } = kline;
  return {
    time: t, close: +c, open: +o, high: +h, low: +l, volume: +v,
  };
};

export const translateResolutionForBinance = (resolution) => TV_RESOLUTIONS_TO_BINANCE[resolution];

export const translateResolutionForTV = (secs) => {
  const intervalMins = (secs / 60).toString();
  let tvInterval = intervalMins;
  if (intervalMins === '1440') { // 1d
    tvInterval = 'D';
  } else if (intervalMins === '10080') { // 1w
    tvInterval = '1W';
  } else if (intervalMins === '43200') { // 1w
    tvInterval = '1M';
  }
  return tvInterval;
};

export class DataFeed {
  constructor(symbols = [], baseUri = REST_BASE_URI) {
    this.symbols = symbols;
    this.baseUri = baseUri;
    this.sockets = new Map();
  }

  // ### onReady(callback)
  // `callback`: function(configurationData)
  //     `configurationData`: object (see below)
  //          exchanges
  //          symbols_types
  //          supported_resolutions
  //          supports_marks
  //          supports_timescale_marks
  //          supports_time
  //          futures_regex
  onReady = (callback) => {
    setTimeout(() => {
      callback({
        exchanges: [],
        symbols_types: [],
        supported_resolutions: Object.keys(TV_RESOLUTIONS_TO_BINANCE),
        supports_marks: false, // TODO: maybe
        supports_timescale_marks: false, // TODO: maybe
        supports_time: true,
      });
    }, 0);
  };

  // ### searchSymbols(userInput, exchange, symbolType, onResultReadyCallback)
  // `userInput`: string. It is text entered by user in the symbol search field.
  // `exchange`: string. The requested exchange (chosen by user).
  //     Empty value means no filter was specified.
  // `symbolType`: string. The requested symbol type: `index`, `stock`, `forex`, etc.
  //     Empty value means no filter was specified.
  // `onResultReadyCallback`: function(result)
  //     `result`: array (see below)
  //        "symbol": "<short symbol name>",
  //        "full_name": "<full symbol name>", // e.g. BTCE:BTCUSD
  //        "description": "<symbol description>",
  //        "exchange": "<symbol exchange name>",
  //        "ticker": "<symbol ticker name, optional>",
  //        "type": "stock" // or "futures" or "crypto" or "forex" or "index"
  searchSymbols = (input, exchange, symbolType, callback) => {
    if (__DEV__) {
      console.log('searchSymbols called with:', input);
    }
    const matches = this.symbols.filter(({ symbol }) => symbol.includes(input));
    const mapped = matches.map(match => ({
      symbol: match.symbol,
      full_name: match.symbol,
      description: match.symbol,
      exchange: 'BINANCE',
      type: 'crypto',
    }));
    if (__DEV__) {
      console.log('TradingView symbol matches', mapped);
    }
    callback(mapped);
  };

  // ### resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback)
  // `symbolName`: string. Symbol name or `ticker` if provided.
  // `onSymbolResolvedCallback`: function([SymbolInfo](Symbology#symbolinfo-structure))
  // `onResolveErrorCallback`: function(reason)
  // Charting Library calls this fn when it needs [SymbolInfo](Symbology#symbolinfo-structure)
  // by symbol name.
  resolveSymbol = (symbol, callback, errorCallback) => {
    if (__DEV__) {
      console.log('resolveSymbol called with:', symbol);
    }
    symbol = symbol.replace('BINANCE:', '');
    const matches = this.symbols.filter(({ symbol: symb }) => symb === symbol);
    if (!matches.length) {
      errorCallback('not found');
      return;
    }
    const match = matches[0];
    const symbolFilter = match.filters.find((filter) => filter.tickSize || filter.stepSize);
    const tickSize = symbolFilter.stepSize || symbolFilter.tickSize;
    const resolved = {
      description: match.symbol,
      fractional: false, // `fractional` for common prices is `false` or it can be skipped.
      has_intraday: true,
      minmov: 1, // `minmov` is a number of units that make up one tick.
      minmove2: 0, // `minmove2` for common prices is `0` or it can be skipped.
      name: match.symbol,
      // `pricescale` defines number of decimal places.
      // Actually it is `10^number-of-decimal-places`.
      pricescale: 10 ** Math.abs(Math.log10(tickSize)),
      session: '24x7',
      timezone: EXCHANGE_TIMEZONE,
      type: 'crypto',
      // volume_precision: 2,
    };
    if (__DEV__) {
      console.log('resolved symbol:', resolved);
    }
    // TODO: fill this out properly
    setTimeout(() => {
      callback(resolved);
    }, 0);
  };

  // ### getBars(symbolInfo, resolution, from, to, onHistoryCallback,
  //              onErrorCallback, firstDataRequest)
  // `symbolInfo`: [SymbolInfo](Symbology#symbolinfo-structure) object
  // `resolution`: string
  // `from`: unix timestamp, leftmost required bar time
  // `to`: unix timestamp, rightmost required bar time
  // `onHistoryCallback`: function(array of `bar`s, `meta` = `{ noData = false }`)
  //     `bar`: object `{time, close, open, high, low, volume}`
  //     `meta`: object `{noData = true | false, nextTime - unix time}`
  // `onErrorCallback`: function(reason)
  // `firstDataRequest`: boolean to identify the first call of this method for the
  //     particular symbol resolution.
  //     When it is set to `true` you can ignore `to` (which depends on browser's `Date.now()`)
  //     and return bars up to the latest bar.
  getBars = async ({ name: symbol }, resolution, from, to, callback, onError) => {
    const interval = translateResolutionForBinance(resolution);
    // this api is hard limited to return 1000 bars max, regardless of `limit`
    const limit = 1000; // never more than 1000!
    const getUri = (startTime) => `${this.baseUri}` +
      '/api/v1/klines' +
      `?symbol=${symbol}` +
      `&interval=${interval}` +
      `&limit=${limit}` +
      `&startTime=${startTime}` +
      `&endTime=${to * 1000}`;
    let lastCount = -1;
    let lastTime = from * 1000;
    let errored = false;
    const bars = [];
    // deal with the 1000 hard limit by making multiple requests
    while (lastCount === -1 || lastCount === limit) {
      try {
        const isFirst = lastCount === -1;
        // +1 to not get duplicate bars
        const res = await fetch(getUri(isFirst ? lastTime : lastTime + 1));
        const batch = await res.json();
        if (!batch || !batch.length) break;
        const [time] = batch[batch.length - 1];
        lastTime = time;
        lastCount = batch.length;
        bars.push(...batch.map(convertKlineArrToBar));
      } catch (err) {
        errored = true;
        onError(err);
        break;
      }
    }
    if (!errored) {
      callback(bars, { noData: !bars.length });
    }
  };

  // ### subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID,
  //                    onResetCacheNeededCallback)
  // Charting Library calls this function when it wants to receive real-time updates for a symbol.
  // The Library assumes that you will call `onRealtimeCallback` every time you want to update
  // the most recent bar or to add a new one.
  // `symbolInfo`: [SymbolInfo](Symbology#symbolinfo-structure) object
  // `resolution`: string
  // `onRealtimeCallback`: function(bar)
  //     `bar`: object `{time, close, open, high, low, volume}`
  // `subscriberUID`: object
  // `onResetCacheNeededCallback` *(since version 1.7)*: function() to be executed
  //    when bar data has changed
  subscribeBars = async ({ name: symbol }, resolution, onMessage, uid, onResetNeeded) => {
    if (this._lastBarsSymbol &&
        this._lastBarsSymbol !== symbol) {
      onResetNeeded();
      return;
    }
    this._lastBarsSymbol = symbol;
    const interval = translateResolutionForBinance(resolution);
    const klinesWsUri = await getKlinesWsUri(symbol, interval);
    this.sockets.set(uid, new WebSocket(klinesWsUri, {
      onopen: () => {},
      onmessage: ({ data: json }) => {
        if (!json || !json.length) {
          console.warn('TradingView feed: received kline without data!', json);
          return;
        }
        const { data } = JSON.parse(json);
        onMessage(convertKlineObjToBar(data));
      },
      onreconnect: () => { onResetNeeded(); },
      onmaximum: () => {},
      onclose: () => {
        console.warn('TradingView feed: WebSocket data closed.');
      },
      onerror: err => {
        console.error('TradingView feed: WebSocket error:', err);
      },
    }));
  };

  // ### unsubscribeBars(subscriberUID)
  // `subscriberUID`: object
  // Charting Library calls this function when it doesn't want to receive updates for
  // this subscriber any more. `subscriberUID` will be the same object that Library passed
  // to `subscribeBars` before.
  unsubscribeBars = (uid) => {
    const socket = this.sockets.get(uid);
    if (!socket) {
      console.warn('TradingView feed: unsubscribeBars called but socket not found.');
      return;
    }
    socket.close(1e3, 'Requested by TradingView');
  };

  // ### calculateHistoryDepth(resolution, resolutionBack, intervalBack)
  // *Optional.*
  // `resolution`: requested symbol resolution
  // `resolutionBack`: time period types. Supported values are: `D` | `M`
  // `intervalBack`: amount of `resolutionBack` periods that the Charting Library is
  // going to request
  // calculateHistoryDepth = (resolution, resolutionBack, intervalBack) => ({
  //   resolutionBack,
  //   // 1000 bars hard limit on the API.
  //   intervalBack: intervalBack > 1000 ? 999 : intervalBack,
  // });

  // ### getServerTime(callback)
  // This function is called if the configuration flag `supports_time` is set to `true` when
  // the Charting Library needs to know the server time.
  // `callback`: function(unixTime)
  getServerTime = async (callback) => {
    const uri = `${this.baseUri}${SERVERTIME_API_PATH}`;
    try {
      const res = await makeApiRequest(uri);
      const unixTime = res.serverTime;
      const secs = Math.floor(parseInt(unixTime, 10) / 1000);
      callback(secs);
    } catch (err) {
      callback(Date.now());
      console.error('TradingView feed: getServerTime called but there was an error!');
    }
  };

  // ### calculateHistoryDepth(resolution, resolutionBack, intervalBack)
  // *Optional.*
  // ...

  // ### getMarks(symbolInfo, startDate, endDate, onDataCallback, resolution)
  // *Optional.*

  // ### getTimescaleMarks(symbolInfo, startDate, endDate, onDataCallback, resolution)
  // *Optional.*
}

export const generateTradingViewConfig = ({
  containerId,
  symbol,
  symbols,
  interval,
  locale,
  theme,
}) => {
  let timezone = EXCHANGE_TIMEZONE;
  // try to use local timezone, if api is available
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {
    // fallback to EXCHANGE_TIMEZONE
  }
  return ({ // eslint-disable-line
    timezone, // user's browser timezone
    autosize: true,
    container_id: containerId,
    datafeed: new DataFeed(symbols),
    debug: !!__DEV__,
    enabled_features: [
      'dont_show_boolean_study_arguments',
      'hide_last_na_study_output',
    ],
    disabled_features: [
      'header_widget',
      'header_symbol_search',
      'symbol_info',
      'header_compare',
      // 'use_localstorage_for_settings',
      // 'save_chart_properties_to_local_storage',
      'header_chart_type',
      'display_market_status',
      'symbol_search_hot_key',
      'compare_symbol',
      'border_around_the_chart',
      'remove_library_container_border',
      'symbol_info',
      'header_interval_dialog_button',
      'show_interval_dialog_on_key_press',
      'volume_force_overlay',
    ],
    interval,
    library_path: `/static/tradingview/${SITE === 'cn' ? 'cn/' : ''}charting_library/`,
    custom_css_url: theme.tradingViewCssPath,
    locale: getTradingViewLocale(locale) || 'en',
    overrides: theme.tradingViewOverrides || {}, // doesn't always work, see below
    studies_overrides: {
      // 'volume.show ma': false,
      'volume.volume.color.0': theme.tradingViewStyle.shortFill,
      'volume.volume.color.1': theme.tradingViewStyle.longFill,
      'Moving Average.precision': 8,
      // 'volume.volume ma.linewidth': 1,
      // 'volume.volume ma.color': theme.tradingViewStyle.maLine, // orange
      // 'volume.volume ma.plottype': 'area', // orange
      // 'volume.volume.transparency': 70,
    },

    style: '1',
    symbol,
    theme: 'Light',
    toolbar_bg: 'rgba(255, 255, 255, 1)', // white
    time_frames: [],
  });
};

export default {
  translateResolutionForBinance,
  translateResolutionForTV,
  DataFeed,
};
